import { ColliderShapeComponent, ComponentIds, Cuboid, HierarchyUtils, PartPair, ReplaceAssemblyPartUtils, TransformUtils } from '@inter-ikea-core/core';
import { PartKey, PlannerEcsMultiSystem, ProductType } from '@inter-ikea-storageone/range';
import { getWorktop, getWorktopRef, hasWorktopCenter, hasWorktopLeft, hasWorktopRight } from '../utils/worktop';
import MoveableWorktopComponent from '../components/MoveableWorktopComponent';
/**
 * To be able to align base cabinets the worktop needs to be moved to the left or right.
 * This system handles the alignment of the worktop when a base cabinet is moved to a candidate snap position.
 */
export class AlignWorktopSystem extends PlannerEcsMultiSystem {
    constructor(coreApi) {
        super(coreApi, 'AlignWorktopSystem', [
            ComponentIds.IS_DRAGGED_COMPONENT_ID,
            ComponentIds.IN_SCENE_COMPONENT_ID
        ]);
    }
    onAdd(entity, newComponent) {
        if (!this.coreApi.entities.isProductType(entity, ProductType.KNOXHULT_CORNER_CABINET_ASM) &&
            !entity.hasComponent(MoveableWorktopComponent.id)) {
            return;
        }
        switch (newComponent.typeId) {
            case ComponentIds.IS_DRAGGED_COMPONENT_ID:
                this.removeWorktopCollider(entity);
                break;
            case ComponentIds.IN_SCENE_COMPONENT_ID:
                this.updateWorktopsForCabinetsInScene();
                this.reAddWorktopCollider(entity);
        }
    }
    onUpdate() { }
    onRemove(entity, oldComponent) {
        if (!this.coreApi.entities.isProductType(entity, ProductType.KNOXHULT_CORNER_CABINET_ASM) &&
            !entity.hasComponent(MoveableWorktopComponent.id)) {
            return;
        }
        switch (oldComponent.typeId) {
            case ComponentIds.IS_DRAGGED_COMPONENT_ID:
                this.updateWorktopsForCabinetsInScene();
                this.reAddWorktopCollider(entity);
                break;
            case ComponentIds.IN_SCENE_COMPONENT_ID:
                this.updateWorktopsForCabinetsInScene();
                break;
        }
    }
    /**
     * Remove collider shape component from worktop so it is not
     * in the way when aligning cabinets.
     */
    removeWorktopCollider(cabinetAssembly) {
        const worktop = getWorktop(cabinetAssembly, this.coreApi);
        worktop?.removeComponent(ComponentIds.COLLIDER_SHAPE_COMPONENT_ID);
    }
    /**
     * Re-add collider shape component to worktop
     */
    reAddWorktopCollider(cabinetAssembly) {
        const worktop = getWorktop(cabinetAssembly, this.coreApi);
        if (worktop?.hasComponent(ComponentIds.COLLIDER_SHAPE_COMPONENT_ID)) {
            return;
        }
        const worktopSize = worktop?.getComponent(ComponentIds.TEMPLATE_DATA_COMPONENT_ID)?.size;
        worktopSize && worktop.addComponent(new ColliderShapeComponent(Cuboid.fromSize(worktopSize)));
    }
    /**
     * Iterate the cabinets in scene and move the worktop depending on the alignment.
     */
    updateWorktopsForCabinetsInScene() {
        const cabinets = this.coreApi.entities.getByComponent(MoveableWorktopComponent.id);
        cabinets.forEach(cabinet => {
            const alignedFrames = this.getAlignedFrames(cabinet);
            if (alignedFrames.left) {
                this.handleAlignmentLeft(cabinet, alignedFrames.left);
            }
            else if (alignedFrames.right) {
                this.handleAlignmentRight(cabinet, alignedFrames.right);
            }
            else {
                this.moveWorktopCenter(cabinet);
            }
        });
    }
    /**
     * If cabinet alignes to another cabinet, on same wall, to the left, move worktop to right.
     * Otherwise if alignment is on diffent walls (against corner cabinet) move worktop to center.
     */
    handleAlignmentLeft(guest, host) {
        const rotationGuest = TransformUtils.worldTransform(guest).rotation.toEulerDegrees();
        const rotationHost = TransformUtils.worldTransform(host).rotation.toEulerDegrees();
        if (Math.round(rotationGuest.Y) === Math.round(rotationHost.Y)) {
            this.moveWorktopRight(guest);
        }
        else {
            this.moveWorktopCenter(guest);
        }
    }
    /**
     * If cabinet alignes to another cabinet, on same wall, to the right, move worktop to left.
     * Otherwise if alignment is on diffent walls (against corner cabinet) move worktop to center.
     */
    handleAlignmentRight(guest, host) {
        const rotationGuest = TransformUtils.worldTransform(guest).rotation.toEulerDegrees();
        const rotationHost = TransformUtils.worldTransform(host).rotation.toEulerDegrees();
        if (Math.round(rotationGuest.Y) === Math.round(rotationHost.Y)) {
            this.moveWorktopLeft(guest);
        }
        else {
            this.moveWorktopCenter(guest);
        }
    }
    /**
     * Moves worktop of the frame assembly to left.
     */
    moveWorktopLeft(cabinetAssembly) {
        if (hasWorktopLeft(cabinetAssembly, this.coreApi)) {
            return;
        }
        const worktopRef = getWorktopRef(cabinetAssembly, this.coreApi);
        worktopRef &&
            ReplaceAssemblyPartUtils.replaceProductParts(cabinetAssembly, [
                new PartPair(PartKey.KNOXHULT_WORKTOP_CENTER, ''),
                new PartPair(PartKey.KNOXHULT_WORKTOP_RIGHT, ''),
                new PartPair(PartKey.KNOXHULT_WORKTOP_LEFT, worktopRef)
            ], this.coreApi.getServices());
    }
    /**
     * Moves worktop of the frame assembly to right.
     */
    moveWorktopRight(cabinetAssembly) {
        if (hasWorktopRight(cabinetAssembly, this.coreApi)) {
            return;
        }
        const worktopRef = getWorktopRef(cabinetAssembly, this.coreApi);
        worktopRef &&
            ReplaceAssemblyPartUtils.replaceProductParts(cabinetAssembly, [
                new PartPair(PartKey.KNOXHULT_WORKTOP_CENTER, ''),
                new PartPair(PartKey.KNOXHULT_WORKTOP_LEFT, ''),
                new PartPair(PartKey.KNOXHULT_WORKTOP_RIGHT, worktopRef)
            ], this.coreApi.getServices());
    }
    /**
     * Moves worktop of the frame assembly to center.
     */
    moveWorktopCenter(cabinetAssembly) {
        if (hasWorktopCenter(cabinetAssembly, this.coreApi)) {
            return;
        }
        const worktopRef = getWorktopRef(cabinetAssembly, this.coreApi);
        worktopRef &&
            ReplaceAssemblyPartUtils.replaceProductParts(cabinetAssembly, [
                new PartPair(PartKey.KNOXHULT_WORKTOP_RIGHT, ''),
                new PartPair(PartKey.KNOXHULT_WORKTOP_LEFT, ''),
                new PartPair(PartKey.KNOXHULT_WORKTOP_CENTER, worktopRef)
            ], this.coreApi.getServices());
    }
    /**
     * Returns the current aligned cabinets of the cabinet assembly.
     */
    getAlignedFrames(cabinetAssembly) {
        const alignedFrames = this.coreApi.entities.getAlignedFrames(HierarchyUtils.children(cabinetAssembly)[0], 'align-cabinet-left', 'align-cabinet-right');
        return {
            left: alignedFrames.left ? HierarchyUtils.parent(alignedFrames.left) : null,
            right: alignedFrames.right ? HierarchyUtils.parent(alignedFrames.right) : null
        };
    }
}
//# sourceMappingURL=AlignWorktopSystem.js.map